import { Amplify } from 'aws-amplify';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
  type ConfirmSignInOutput,
  type SignInOutput,
} from 'aws-amplify/auth';
import type { AppConfig } from '../config';
import { roleFromGroups, type AuthBackend, type AuthUser, type SignInOutcome } from './types';

export function createCognitoAuth(config: AppConfig): AuthBackend {
  /** Email of the user currently signing in; names the account in the authenticator app. */
  let pendingUsername = '';

  async function outcomeFromOutput(out: SignInOutput | ConfirmSignInOutput): Promise<SignInOutcome> {
    const step = out.nextStep;
    switch (step.signInStep) {
      case 'DONE':
        return { kind: 'done' };
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return { kind: 'new-password' };
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        return { kind: 'totp' };
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        // Pool requires MFA and this user has no authenticator yet.
        const details = step.totpSetupDetails;
        return {
          kind: 'totp-setup',
          setup: {
            secret: details.sharedSecret,
            uri: details.getSetupUri(config.mspName, pendingUsername).toString(),
          },
        };
      }
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
        // Only TOTP is enabled on the pool; pick it and continue.
        return outcomeFromOutput(await confirmSignIn({ challengeResponse: 'TOTP' }));
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
        return outcomeFromOutput(await confirmSignIn({ challengeResponse: 'TOTP' }));
      default:
        return { kind: 'unsupported', step: step.signInStep };
    }
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: { email: true },
      },
    },
  });

  return {
    async getUser(): Promise<AuthUser | null> {
      let session;
      try {
        session = await fetchAuthSession();
      } catch {
        return null;
      }
      const id = session.tokens?.idToken;
      if (!id) return null;
      const p = id.payload as Record<string, unknown>;
      const rawGroups = p['cognito:groups'];
      const groups = Array.isArray(rawGroups) ? rawGroups.map(String) : [];
      const role = roleFromGroups(groups);
      if (!role) {
        throw new Error('Your account has no dashboard role. Ask your administrator to assign one.');
      }
      const tenantId = typeof p['custom:tenantId'] === 'string' ? (p['custom:tenantId'] as string) : '';
      return {
        sub: String(p.sub ?? ''),
        email: String(p.email ?? ''),
        role,
        isMsp: role === 'msp-admin',
        tenantId: tenantId || undefined,
        groups,
      };
    },
    async signIn(email, password) {
      pendingUsername = email;
      return outcomeFromOutput(await signIn({ username: email, password }));
    },
    async confirmNewPassword(password) {
      return outcomeFromOutput(await confirmSignIn({ challengeResponse: password }));
    },
    async confirmTotp(code) {
      // Answers both a TOTP challenge and the code that completes TOTP setup.
      return outcomeFromOutput(await confirmSignIn({ challengeResponse: code }));
    },
    async signOut() {
      await signOut();
    },
    async requestPasswordReset(email) {
      await resetPassword({ username: email });
    },
    async confirmPasswordReset(email, code, newPassword) {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    },
    async getIdToken() {
      try {
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString() ?? null;
      } catch {
        return null;
      }
    },
    async getMfaStatus() {
      const pref = await fetchMFAPreference();
      return { totpEnabled: (pref.enabled ?? []).includes('TOTP') };
    },
    async setupTotp() {
      const [details, attributes] = await Promise.all([setUpTOTP(), fetchUserAttributes()]);
      const account = attributes.email || pendingUsername || 'user';
      return {
        secret: details.sharedSecret,
        uri: details.getSetupUri(config.mspName, account).toString(),
      };
    },
    async verifyTotpSetup(code) {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED' });
    },
    async disableTotp() {
      await updateMFAPreference({ totp: 'DISABLED' });
    },
  };
}
